// Engagement (streak & rewards) summary hook — react-query, 60s poll +
// refetch-on-focus per Phase 3. Shared by the Engagement page, the dashboard
// preview card, and the header streak dropdown; react-query dedupes the
// underlying request across all three mounted consumers.
//
// A failed/disabled fetch resolves to DISABLED_ENGAGEMENT_SUMMARY
// (pageEnabled: false) rather than throwing, so every consumer can gate on
// `data.pageEnabled` alone and fall back to today's pure-leaderboard render
// without extra error-branching.

import {useMemo} from 'react';
import {useQuery} from '@tanstack/react-query';
import {
  ENGAGEMENT_USE_MOCK,
  fetchEngagementSummary,
} from './leaderboardService';
import {computeEngagementPresentation} from './engagementPresentation';
import {DEFAULT_ENGAGEMENT_SCENARIO, MOCK_FORCE_AT_RISK} from './mockData';
import {DISABLED_ENGAGEMENT_SUMMARY, EngagementSummary} from './types';

const POLL_MS = 60000;

export function useEngagementData() {
  const query = useQuery<EngagementSummary, Error>(
    ['engagementSummary'],
    () => fetchEngagementSummary(),
    {
      refetchInterval: POLL_MS,
      refetchOnWindowFocus: true,
      staleTime: 30000,
      retry: 1,
    },
  );

  const summary: EngagementSummary = query.data ?? DISABLED_ENGAGEMENT_SUMMARY;

  const presentation = useMemo(
    () =>
      computeEngagementPresentation(summary, {
        forceAtRisk: ENGAGEMENT_USE_MOCK
          ? MOCK_FORCE_AT_RISK[DEFAULT_ENGAGEMENT_SCENARIO]
          : undefined,
      }),
    [summary],
  );

  return {
    summary,
    presentation,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
